import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useT } from '../lib/i18n'
import { usePlaceName } from '../lib/placeName'

export interface Place {
  id: string
  name: string
  city_id: string
  city_name: string
  kind: string | null
  /** True for the city itself — "anywhere in Jeddah" is a real answer. */
  is_city: boolean
}

interface CityWithPoints {
  id: string; name: string
  points: { id: string; name: string; kind: string }[]
}

/**
 * The mockup's `placeSelect`: one grouped dropdown, the city as the group
 * and its named places as the options.
 *
 * A native <select> rather than the two sheets an earlier build used. A
 * charter is a journey between two NAMED places, and making someone pick a
 * city and then open a second sheet to say "the airport" turns one decision
 * into three taps. The native control also gives the phone its own wheel,
 * which is faster than anything drawn here.
 *
 * Sizes are the reference's `label.f` and its input: 12px label, 48px
 * control, radius 8, 14px text.
 */
export function usePlaces() {
  return useQuery<Place[]>({
    queryKey: ['places'],
    queryFn: async () => {
      const cities = await api<CityWithPoints[]>('/cities/with-points')
      const out: Place[] = []
      for (const c of cities) {
        out.push({ id: `city:${c.id}`, name: c.name, city_id: c.id,
                   city_name: c.name, kind: null, is_city: true })
        for (const p of c.points) {
          out.push({ id: p.id, name: p.name, city_id: c.id,
                     city_name: c.name, kind: p.kind, is_city: false })
        }
      }
      return out
    },
  })
}

export function PlaceSelect({ label, value, onChange, first = false }: {
  label: string
  value: string
  onChange: (id: string) => void
  /** No top margin: label.f's 16px is between fields, not above the first. */
  first?: boolean
}) {
  const msg = useT()
  const place = usePlaceName()
  const places = usePlaces()

  const cities: { name: string; items: Place[] }[] = []
  for (const p of places.data ?? []) {
    let g = cities.find((c) => c.name === p.city_name)
    if (!g) { g = { name: p.city_name, items: [] }; cities.push(g) }
    g.items.push(p)
  }

  return (
    <label className={`block text-[12px] text-ink-2 ${first ? '' : 'mt-4'}`}>
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-[7px] block min-h-[48px] w-full rounded-lg border border-line
                   bg-surface px-[13px] pe-9 py-3 text-[14px] text-ink
                   focus:ring-2 focus:ring-accent"
      >
        <option value="">{msg('charter.choosePlace')}</option>
        {cities.map((c) => (
          <optgroup key={c.name} label={place(c.name)}>
            {c.items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.is_city ? msg('charter.anywhereInCity', { city: place(c.name) }) : place(p.name)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  )
}
